import type { Cents } from "../domain/money";
import type { DayCountConvention, LoanStructure, AmortizationStartRule } from "../domain/financing";

/**
 * FINANCING (spec §73C/G).
 *
 * A pure, deterministic loan ledger. Given CONFIRMED terms and dated
 * balance events, it computes what a lender's statement would: the drawn
 * balance over time, simple daily interest on the contractual basis, the
 * payment schedule, fees and payoff.
 *
 * The rules that matter:
 *
 *   • Interest accrues on the balance THE CONTRACT names. "Drawn" means the
 *     outstanding drawn principal — never total-commitment × APR × months
 *     when the contract defines a draw-based balance.
 *   • An UNKNOWN term stays unknown. Unknown rate → no interest projected,
 *     and the ledger says so; unknown basis → planning uses the drawn
 *     balance and says so. Ambiguity is never converted into zero-as-fact:
 *     every fallback is a named assumption the caller must display.
 *   • Simple daily interest, ACT/365, half-up rounding to whole cents per
 *     accrual period. Deterministic: same inputs, same ledger.
 */

export type InterestBasis = "drawn" | "committed" | "unknown";
export type PaymentCadence = "monthly" | "at-maturity" | "unknown";

export interface FacilityTerms {
  /** Historic scheduled dues do not imply a payment. */
  asOfDate?: string;
  dayCountConvention?: DayCountConvention | null;
  loanStructure?: LoanStructure | null;
  interestOnlyMonths?: number | null;
  amortizationMonths?: number | null;
  amortizationStartRule?: AmortizationStartRule | null;
  rateChanges?: readonly { date: string; ratePctX100: number }[];
  id: string;
  name: string;
  loanType: string;
  commitmentCents: Cents | null;
  acquisitionFundingCents: Cents | null;
  rehabFacilityCents: Cents | null;
  downPaymentCents: Cents | null;
  interestRatePctX100: number | null;
  interestBasis: InterestBasis;
  interestOnly: boolean;
  originationPointsX100: number | null;
  originationFeeCents: Cents | null;
  otherFeesCents: Cents | null;
  drawFeeCents: Cents | null;
  paymentCadence: PaymentCadence;
  closingDate: string | null;
  firstPaymentDate: string | null;
  maturityDate: string | null;
}

export interface LedgerEvent {
  /** Optional contractual due being settled; supports early/late interest payments. */
  dueDate?: string | null;
  date: string;
  kind: "draw" | "principal-payment" | "payoff" | "interest-payment" | "fee-payment";
  amountCents: Cents;
  label: string;
}

export interface InterestPayment {
  /** A horizon valuation, not a contractual cash due date. */
  accrualOnly?: boolean;
  id?: string;
  principalCents?: Cents;
  feeCents?: Cents;
  totalCents?: Cents;
  paidInterestCents?: Cents;
  /** The date the payment falls due. */
  date: string;
  interestCents: Cents;
  /** Accrued from the day after the previous payment through this one. */
  from: string;
  to: string;
}

export interface LedgerPeriod {
  from: string;
  to: string;
  balanceCents: Cents;
  interestCents: Cents;
}

export interface LoanLedger {
  /** Explicit fee facts; populated by the source-event assembler. */
  recordedFeesCents?: Cents;
  commitmentCents?: Cents | null;
  revolving?: boolean;
  balloonCents?: Cents;
  facilityId: string;
  periods: LedgerPeriod[];
  payments: InterestPayment[];
  totalInterestCents: Cents;
  endingBalanceCents: Cents;
  peakBalanceCents: Cents;
  /** The principal a payoff event retired (0 when the loan is never paid off). */
  payoffCents: Cents;
  /** Origination points resolved against the commitment, plus flat fees. */
  originationCents: Cents;
  /** Every fallback the ledger had to take, in words the UI must show. */
  assumptions: string[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function dayNumber(date: string): number {
  return Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);
}

export function addDays(date: string, days: number): string {
  return new Date((dayNumber(date) + days) * DAY_MS).toISOString().slice(0, 10);
}

/** Same day-of-month one month later, clamped to the month's end. */
export function addMonth(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + 1);
  const daysInMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, daysInMonth));
  return d.toISOString().slice(0, 10);
}

const halfUp = (value: number): number => Math.round(value);

/** Simple interest for `days` days on `balance` at pct-x100 annual, ACT/365. */
export function interestFor(balanceCents: Cents, ratePctX100: number, days: number): Cents {
  if (balanceCents <= 0 || ratePctX100 <= 0 || days <= 0) return 0;
  return halfUp((balanceCents * ratePctX100 * days) / (10000 * 365));
}

export function originationCentsFor(terms: FacilityTerms): { cents: Cents; assumptions: string[] } {
  const assumptions: string[] = [];
  let cents = 0;
  if (terms.originationPointsX100 !== null) {
    const basis = terms.commitmentCents ?? terms.acquisitionFundingCents;
    if (basis === null) {
      assumptions.push(`${terms.name}: origination points are known but the loan amount is not — points not projected.`);
    } else {
      if (terms.commitmentCents === null) {
        assumptions.push(`${terms.name}: points computed on the acquisition funding because the total commitment is unknown.`);
      }
      // pointsX100 is percent-x100: 2 points = 200 → basis × 200 / 10000.
      cents += halfUp((basis * terms.originationPointsX100) / 10000);
    }
  }
  cents += terms.originationFeeCents ?? 0;
  cents += terms.otherFeesCents ?? 0;
  return { cents, assumptions };
}

/**
 * The ledger. Events must be dated; the ledger accrues between them and
 * emits the payment schedule through `throughDate` (or payoff, whichever
 * comes first).
 */
function computeLegacyLoanLedger(
  terms: FacilityTerms,
  events: readonly LedgerEvent[],
  throughDate: string,
): LoanLedger {
  const assumptions: string[] = [];
  const rate = terms.interestRatePctX100;
  if (rate === null) {
    assumptions.push(`${terms.name}: interest rate unknown — no interest is projected until the term is confirmed.`);
  }
  let basis: InterestBasis = terms.interestBasis;
  if (basis === "unknown") {
    basis = "drawn";
    assumptions.push(`${terms.name}: interest basis not confirmed — planning accrues on the drawn balance.`);
  }
  if (basis === "committed" && terms.commitmentCents === null) {
    basis = "drawn";
    assumptions.push(`${terms.name}: interest is on the committed amount but the commitment is unknown — planning accrues on the drawn balance.`);
  }

  const ordered = [...events].sort(
    (a, b) => a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind) || a.label.localeCompare(b.label),
  );
  // The ledger starts when money first moved: a draw funded BEFORE the
  // recorded closing date is still a balance from its own date (found
  // live — a pre-closing funded draw silently accrued nothing).
  const firstEvent = ordered[0]?.date ?? null;
  const start =
    terms.closingDate !== null && firstEvent !== null
      ? (firstEvent < terms.closingDate ? firstEvent : terms.closingDate)
      : (terms.closingDate ?? firstEvent ?? throughDate);

  // Payment dates through the horizon.
  let cadence: PaymentCadence = terms.paymentCadence;
  if (cadence === "unknown") {
    cadence = "monthly";
    assumptions.push(`${terms.name}: payment cadence not confirmed — planning assumes monthly interest payments.`);
  }
  const paymentDates: string[] = [];
  const payoffDate = ordered.find((e) => e.kind === "payoff")?.date ?? null;
  const end = payoffDate && payoffDate < throughDate ? payoffDate : throughDate;
  if (cadence === "monthly") {
    let due = terms.firstPaymentDate ?? addMonth(start);
    while (due <= end) {
      paymentDates.push(due);
      due = addMonth(due);
    }
  }
  // at-maturity (or the payoff itself): everything settles at the end.
  if (payoffDate && (paymentDates.length === 0 || paymentDates[paymentDates.length - 1] !== payoffDate)) {
    paymentDates.push(end);
  } else if (!payoffDate && cadence === "at-maturity" && terms.maturityDate && terms.maturityDate <= end) {
    paymentDates.push(terms.maturityDate);
  }

  // Walk day boundaries: balance changes on event dates, interest accrues
  // between, payments collect accrued interest.
  const boundaries = [...new Set([start, ...ordered.map((e) => e.date), ...paymentDates, end])]
    .filter((d) => d >= start && d <= end)
    .sort();

  const periods: LedgerPeriod[] = [];
  const payments: InterestPayment[] = [];
  let balance = 0;
  let peak = 0;
  let payoffCents = 0;
  let accrued = 0;
  let accruedFrom = start;
  let totalInterest = 0;

  const applyEventsOn = (date: string) => {
    for (const event of ordered) {
      if (event.date !== date) continue;
      if (event.kind === "draw") balance += event.amountCents;
      else if (event.kind === "principal-payment") balance -= event.amountCents;
      else if (event.kind === "payoff") {
        // Same-day draws sort before the payoff, so the payoff retires the
        // balance INCLUDING anything drawn that day.
        payoffCents = balance;
        balance = 0;
      }
      peak = Math.max(peak, balance);
    }
  };

  applyEventsOn(start);
  const paymentSet = new Set(paymentDates);

  for (let i = 0; i < boundaries.length - 1; i++) {
    const from = boundaries[i];
    const to = boundaries[i + 1];
    const days = dayNumber(to) - dayNumber(from);
    const interestBase = basis === "committed" ? (terms.commitmentCents ?? balance) : balance;
    const interest = rate === null ? 0 : interestFor(interestBase, rate, days);
    periods.push({ from, to, balanceCents: balance, interestCents: interest });
    accrued += interest;
    totalInterest += interest;
    // Events (draws etc.) land at the START of a day: apply before the day
    // is charged, so a draw accrues from its own date.
    applyEventsOn(to);
    if (paymentSet.has(to)) {
      payments.push({ date: to, interestCents: accrued, from: accruedFrom, to });
      accrued = 0;
      accruedFrom = to;
    }
  }
  // Anything still accrued at the horizon is carried, not silently paid.
  if (accrued > 0) {
    payments.push({ date: end, interestCents: accrued, from: accruedFrom, to: end, accrualOnly: true });
  }

  const origination = originationCentsFor(terms);
  assumptions.push(...origination.assumptions);

  return {
    facilityId: terms.id,
    periods,
    payments,
    totalInterestCents: totalInterest,
    endingBalanceCents: balance,
    peakBalanceCents: peak,
    payoffCents,
    originationCents: origination.cents,
    assumptions,
  };
}

/** Anchored calendar months: Jan 31 → Feb 28 → Mar 31, never Mar 28. */
export function loanMonthDate(anchor: string, offset: number): string {
  const d = new Date(`${anchor}T00:00:00Z`), day = d.getUTCDate();
  d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + offset);
  d.setUTCDate(Math.min(day, new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()));
  return d.toISOString().slice(0, 10);
}

const integerRatio = (numerator: bigint, denominator: bigint): number => Number((numerator + denominator / 2n) / denominator);

/** 30/360 is explicitly European 30E/360 (both day-31 values become 30).
 * Monthly uses the actual contractual cycle, so a full cycle is APR / 12. */
export function loanPeriodInterest(balance: Cents, rate: number, from: string, to: string, convention: DayCountConvention, cycleFrom = from, cycleTo = to): Cents {
  if (balance <= 0 || rate <= 0 || to <= from) return 0;
  const days = dayNumber(to) - dayNumber(from);
  let numerator = BigInt(balance) * BigInt(rate), denominator = 10000n;
  if (convention === "monthly") {
    numerator *= BigInt(days); denominator *= 12n * BigInt(Math.max(1, dayNumber(cycleTo) - dayNumber(cycleFrom)));
  } else if (convention === "30-360") {
    const a = new Date(`${from}T00:00:00Z`), b = new Date(`${to}T00:00:00Z`);
    const count = (b.getUTCFullYear() - a.getUTCFullYear()) * 360 + (b.getUTCMonth() - a.getUTCMonth()) * 30 + Math.min(30, b.getUTCDate()) - Math.min(30, a.getUTCDate());
    numerator *= BigInt(count); denominator *= 360n;
  } else { numerator *= BigInt(days); denominator *= convention === "act360" ? 360n : 365n; }
  return integerRatio(numerator, denominator);
}

/** Fixed contractual monthly payment, exact rational arithmetic and half-up cents. */
export function amortizedPaymentCents(principal: Cents, ratePctX100: number, months: number): Cents {
  if(!Number.isSafeInteger(principal)||principal<0||!Number.isSafeInteger(ratePctX100)||ratePctX100<0||!Number.isSafeInteger(months)||months<1||months>1200) throw new Error("Loan payment requires exact nonnegative cents/rate and 1–1200 months.");
  const p=BigInt(principal),r=BigInt(ratePctX100),n=BigInt(months),base=120000n;
  if(r===0n)return Number((p+n-1n)/n);
  const growth=(base+r)**n,flat=base**n;
  return integerRatio(p*r*growth,base*(growth-flat));
}

/** One ledger, two compatibility modes. Unextended legacy contracts retain
 * their existing figures. New explicitly selected structures use §93 terms. */
export function computeLoanLedger(terms: FacilityTerms, events: readonly LedgerEvent[], throughDate: string): LoanLedger {
  if (!terms.dayCountConvention && !terms.loanStructure && !terms.rateChanges?.length && !events.some(e => e.kind === "interest-payment" || e.kind === "fee-payment")) return computeLegacyLoanLedger(terms, events, throughDate);
  const assumptions: string[] = [];
  if (terms.interestRatePctX100 === null) assumptions.push(`${terms.name}: interest rate unknown — interest is not a verified zero.`);
  if (terms.interestBasis === "unknown") assumptions.push(`${terms.name}: interest basis unconfirmed — planning uses drawn principal.`);
  if (terms.paymentCadence === "unknown") assumptions.push(`${terms.name}: payment cadence unconfirmed — planning uses monthly due dates.`);
  const ordered = [...events].sort((a,b) => a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind));
  const start = [terms.closingDate, ordered[0]?.date].filter((d): d is string => !!d).sort()[0] ?? throughDate;
  const payoffDate = ordered.find(e => e.kind === "payoff")?.date;
  const end = [throughDate, payoffDate, terms.maturityDate].filter((d): d is string => !!d && d >= start).sort()[0];
  const firstDue = terms.firstPaymentDate ?? loanMonthDate(start, 1);
  const dueDates: string[] = [];
  if (terms.paymentCadence !== "at-maturity") for (let i=0;i<1200;i++) {
    const date = loanMonthDate(firstDue, i); if (date > end) break; if (date >= start) dueDates.push(date);
  }
  const due = new Set(dueDates);
  if (terms.maturityDate && terms.maturityDate <= end) due.add(terms.maturityDate);
  if (payoffDate && payoffDate <= end) due.add(payoffDate);
  const changes = [...(terms.rateChanges ?? [])].sort((a,b)=>a.date.localeCompare(b.date));
  const boundaries = [...new Set([start,end,...ordered.map(e=>e.date),...changes.map(r=>r.date),...due])].filter(d=>d>=start&&d<=end).sort();
  const structure = terms.loanStructure ?? (terms.interestOnly ? "interest-only" : "amortizing");
  const io = structure === "io-amortizing" ? terms.interestOnlyMonths ?? null : 0;
  const contractMonths = terms.amortizationMonths ?? null;
  const transitionKnown = io !== null && (structure !== "io-amortizing" || !!terms.amortizationStartRule);
  const amortCount = contractMonths === null ? null : terms.amortizationStartRule === "includes-io" ? contractMonths - (io ?? 0) : contractMonths;
  const amortizing = structure === "amortizing" || structure === "io-amortizing" || (structure === "balloon" && contractMonths !== null);
  if (amortizing && (!transitionKnown || !amortCount || amortCount <= 0)) assumptions.push(`${terms.name}: amortization term or IO transition rule unknown — principal installments are not invented.`);
  let balance=0, peak=0, payoffCents=0, accrued=0, totalInterest=0, paidInterest=0, paidPrincipal=0, cycleFrom=start, dueIndex=0, paymentAmount:number|null=null, previousRate:number|null=null, balloonCents=0;
  const periods:LedgerPeriod[]=[], payments:InterestPayment[]=[];
  const apply = (date:string) => {
    for(const event of ordered.filter(e=>e.date===date)) {
      if(event.kind === "draw") balance += event.amountCents;
      else if(event.kind === "principal-payment") { balance -= event.amountCents; paidPrincipal += event.amountCents; }
      else if(event.kind === "interest-payment" && !event.dueDate) paidInterest += event.amountCents;
      else if(event.kind === "payoff") { payoffCents += balance; balance=0; }
      if(balance < 0) throw new Error("Principal repayment exceeds the outstanding funded balance.");
      peak=Math.max(peak,balance);
    }
  };
  apply(start);
  for(let i=0;i<boundaries.length-1;i++) {
    const from=boundaries[i],to=boundaries[i+1];
    const rate=changes.filter(c=>c.date<=from).at(-1)?.ratePctX100 ?? terms.interestRatePctX100;
    const nextDue=dueDates.find(d=>d>=to) ?? loanMonthDate(firstDue,dueDates.length);
    const interest=rate===null?0:loanPeriodInterest(terms.interestBasis === "committed" ? terms.commitmentCents ?? balance : balance,rate,from,to,terms.dayCountConvention ?? "act365",cycleFrom,nextDue);
    periods.push({from,to,balanceCents:balance,interestCents:interest}); accrued+=interest; totalInterest+=interest;
    apply(to);
    if(due.has(to)) {
      dueIndex++;
      let principal=0;
      const balloon = to === terms.maturityDate;
      if(balloon) { principal=balance; balloonCents=balance; if (!terms.asOfDate || to > terms.asOfDate) balance=0; }
      else if(amortizing && transitionKnown && amortCount && amortCount>0 && dueIndex>(io??0) && rate!==null && balance>0) {
        const monthsLeft=Math.max(1,amortCount-(dueIndex-(io??0)-1));
        if(paymentAmount===null || previousRate!==rate) {
          paymentAmount=amortizedPaymentCents(balance,rate,monthsLeft);
          previousRate=rate;
        }
        principal=Math.min(balance,Math.max(0,paymentAmount-accrued-paidPrincipal)); if (!terms.asOfDate || to > terms.asOfDate) balance-=principal;
      }
      paidInterest += ordered.filter(e=>e.kind === "interest-payment" && e.dueDate === to && e.date <= throughDate).reduce((sum,e)=>sum+e.amountCents,0);
      const interestDue=Math.max(0,accrued-paidInterest);
      payments.push({id:`${terms.id}:due:${to}`,date:to,from:cycleFrom,to,interestCents:interestDue,principalCents:principal,feeCents:0,totalCents:interestDue+principal,paidInterestCents:paidInterest});
      accrued=0;paidInterest=0;paidPrincipal=0;cycleFrom=to;
    }
  }
  if(accrued>0) payments.push({id:`${terms.id}:accrual:${end}`,date:end,from:cycleFrom,to:end,interestCents:Math.max(0,accrued-paidInterest),principalCents:0,feeCents:0,totalCents:Math.max(0,accrued-paidInterest),paidInterestCents:paidInterest,accrualOnly:true});
  if(terms.asOfDate && terms.maturityDate && terms.maturityDate <= terms.asOfDate && balance>0) assumptions.push(`${terms.name}: unpaid maturity balance remains outstanding; post-maturity interest and penalties require confirmed extension terms.`);
  const origination=originationCentsFor(terms);assumptions.push(...origination.assumptions);
  return {facilityId:terms.id,periods,payments,totalInterestCents:totalInterest,endingBalanceCents:balance,peakBalanceCents:peak,payoffCents,originationCents:origination.cents,assumptions,balloonCents};
}
